import MonthlyReportCard from './MonthlyReportCard';
import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  alpha,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  AccessTime,
  Cancel,
  EventBusy,
  HolidayVillage,
  Work,
  Schedule,
  People,
  Today,
  DateRange,
  CalendarViewMonth,
  BarChart,
  Visibility,
  Coffee,
} from '@mui/icons-material';
import { API_URL } from '../services/api';

// Base URL for selfies (strip /api from API_URL)
const baseURL = API_URL ? API_URL.replace('/api', '') : '';

// Helper function to get month name
const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month];
};

// Selfie Image Component with error handling
const SelfieImage = ({ src, alt, size = 150 }) => {
  const [imgError, setImgError] = useState(false);
  const fullUrl = src ? `${baseURL}${src}` : null;

  if (!src || imgError || !fullUrl) {
    return (
      <Box sx={{
        width: size,
        height: size,
        bgcolor: alpha('#D4AF37', 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 2,
        color: '#B4C4A8'
      }}>
        <Typography variant="caption">No image</Typography>
      </Box>
    );
  }

  return (
    <img
      src={fullUrl}
      alt={alt}
      className="report-image"
      style={{ '--report-image-size': size }}
      onClick={() => window.open(fullUrl, '_blank')}
      onError={() => setImgError(true)}
    />
  );
};

const ReportContent = ({
  loading,
  reportType,
  reportData,
  selectedYear,
  selectedMonth,
  customStartDate,
  customEndDate,
  isAdmin,
  selectedEmployeesBulk,
  setSelectedEmployeesBulk,
  bulkActionLoading,
  handleBulkLeaveRequest,
  handleBulkPrintLocal,
  activeTab,
  setActiveTab,
  employees,
  handleMarkEmployeePresent,
  handleMarkEmployeeAbsent,
  handleMarkEmployeeLeave,
  handleViewEmployeeDetail,
  handleMarkSelectedPresent,
  handleMarkSelectedAbsent,
  loadEmployeeAttendance,
  handleMarkEmployeeStatusWithTimes,
  reportDate,
}) => {
  // State for daily report detail dialog
  const [dailyDetailOpen, setDailyDetailOpen] = useState(false);
  const [selectedDailyRecord, setSelectedDailyRecord] = useState(null);

  // State for day breakdown dialog
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [selectedDayAttendance, setSelectedDayAttendance] = useState(null);
  const [loadingDayDetail, setLoadingDayDetail] = useState(false);

  // State for employee full report dialog
  const [employeeReportOpen, setEmployeeReportOpen] = useState(false);
  const [selectedEmployeeReport, setSelectedEmployeeReport] = useState(null);
  const [loadingEmployeeReport, setLoadingEmployeeReport] = useState(false);

  // State for status dropdown menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuEmployee, setStatusMenuEmployee] = useState(null);
  const [statusMenuDate, setStatusMenuDate] = useState(null);

  // Format working hours
  const formatWorkingHours = useCallback((minutes) => {
    if (!minutes && minutes !== 0) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }, []);

  const getTodayDateString = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const sortDailyReportDays = useCallback((days) => {
    const today = getTodayDateString();

    return [...days].sort((a, b) => {
      if (a.date === today) return -1;
      if (b.date === today) return 1;

      const aIsPastOrToday = a.date <= today;
      const bIsPastOrToday = b.date <= today;

      if (aIsPastOrToday && !bIsPastOrToday) return -1;
      if (!aIsPastOrToday && bIsPastOrToday) return 1;

      return aIsPastOrToday
        ? b.date.localeCompare(a.date)
        : a.date.localeCompare(b.date);
    });
  }, [getTodayDateString]);

  // Format date
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateStr;
    }
  }, []);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'present': return { bg: alpha('#4CAF50', 0.15), color: '#81C784', icon: <CheckCircle sx={{ fontSize: 16 }} /> };
      case 'late': return { bg: alpha('#FF9800', 0.15), color: '#FFB74D', icon: <AccessTime sx={{ fontSize: 16 }} /> };
      case 'on_leave': return { bg: alpha('#2196F3', 0.15), color: '#90CAF9', icon: <EventBusy sx={{ fontSize: 16 }} /> };
      case 'absent': return { bg: alpha('#F44336', 0.15), color: '#EF9A9A', icon: <Cancel sx={{ fontSize: 16 }} /> };
      case 'holiday': return { bg: alpha('#9C27B0', 0.15), color: '#CE93D8', icon: <HolidayVillage sx={{ fontSize: 16 }} /> };
      default: return { bg: alpha('#9E9E9E', 0.15), color: '#B4C4A8', icon: null };
    }
  }, []);

  // Calculate leave days
  const calculateLeaveDays = useCallback((leaves, month, year) => {
    if (!leaves || !Array.isArray(leaves)) return 0;
    let totalDays = 0;
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    leaves.forEach(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      const effectiveStart = start > monthStart ? start : monthStart;
      const effectiveEnd = end < monthEnd ? end : monthEnd;
      if (effectiveStart <= effectiveEnd) {
        const diffTime = Math.abs(effectiveEnd - effectiveStart);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        totalDays += diffDays;
      }
    });
    return totalDays;
  }, []);

  // Open daily detail
  const openDailyDetail = useCallback((employee, dayRecords) => {
    const record = dayRecords.find(r => r.employee?.id === employee.id);
    setSelectedDailyRecord(record || null);
    setDailyDetailOpen(true);
  }, []);

  // Open day detail
  const openDayDetail = useCallback(async (day, employeeId = null) => {
    if (!loadEmployeeAttendance) {
      console.error('loadEmployeeAttendance function not provided');
      setSelectedDayAttendance([]);
      setLoadingDayDetail(false);
      setDayDetailOpen(true);
      return;
    }
    setLoadingDayDetail(true);
    try {
      const attendance = await loadEmployeeAttendance(employeeId);
      const filteredAttendance = attendance.filter(a =>
        new Date(a.date).toISOString().split('T')[0] === day
      );
      setSelectedDayAttendance(filteredAttendance);
    } catch (error) {
      console.error('Error loading day detail:', error);
      setSelectedDayAttendance([]);
    } finally {
      setLoadingDayDetail(false);
      setDayDetailOpen(true);
    }
  }, [loadEmployeeAttendance]);

  // Open employee report
  const openEmployeeReport = useCallback(async (employee) => {
    if (!loadEmployeeAttendance) {
      console.error('loadEmployeeAttendance function not provided');
      setSelectedEmployeeReport([]);
      setLoadingEmployeeReport(false);
      setEmployeeReportOpen(true);
      return;
    }
    setLoadingEmployeeReport(true);
    try {
      const attendance = await loadEmployeeAttendance(employee.id);
      setSelectedEmployeeReport(attendance);
    } catch (error) {
      console.error('Error loading employee report:', error);
      setSelectedEmployeeReport([]);
    } finally {
      setLoadingEmployeeReport(false);
      setEmployeeReportOpen(true);
    }
  }, [loadEmployeeAttendance]);

  // Open status menu
  const openStatusMenu = useCallback((event, employeeId, date) => {
    event.preventDefault();
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuEmployee(employeeId);
    setStatusMenuDate(date);
  }, []);

  // Close status menu
  const closeStatusMenu = useCallback(() => {
    setStatusMenuAnchor(null);
    setStatusMenuEmployee(null);
    setStatusMenuDate(null);
  }, []);

  // Handle status change from menu
  const handleStatusChangeFromMenu = useCallback((newStatus) => {
    if (statusMenuEmployee && statusMenuDate && handleMarkEmployeeStatusWithTimes) {
      handleMarkEmployeeStatusWithTimes(statusMenuEmployee, statusMenuDate, newStatus);
    }
    closeStatusMenu();
  }, [statusMenuEmployee, statusMenuDate, handleMarkEmployeeStatusWithTimes, closeStatusMenu]);

  // Daily Detail Dialog component (shared between both daily report branches)
  const renderDailyDetailDialog = () => {
    const dateStr = selectedDailyRecord?.date || selectedDailyRecord?.employee?.date || '';
    return (
      <Dialog
        open={dailyDetailOpen}
        onClose={() => setDailyDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgb(10, 25, 41)',
            border: '2px solid #D4AF37',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
          Attendance Details - {selectedDailyRecord?.employee?.first_name || ''} {selectedDailyRecord?.employee?.last_name || ''} {dateStr ? `(${formatDate(dateStr)})` : ''}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDailyRecord ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', fontSize: 24, fontWeight: 600 }}>
                  {selectedDailyRecord.employee?.first_name?.[0]}{selectedDailyRecord.employee?.last_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                    {selectedDailyRecord.employee?.first_name} {selectedDailyRecord.employee?.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                    {selectedDailyRecord.employee?.employee_id} | {selectedDailyRecord.employee?.department}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={selectedDailyRecord.status?.replace('_', ' ').toUpperCase()}
                      sx={{
                        bgcolor: getStatusColor(selectedDailyRecord.status).bg,
                        color: getStatusColor(selectedDailyRecord.status).color,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Login Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {selectedDailyRecord.login_time ? new Date(selectedDailyRecord.login_time).toLocaleTimeString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Logout Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {selectedDailyRecord.logout_time ? new Date(selectedDailyRecord.logout_time).toLocaleTimeString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                  <Typography variant="body2" sx={{ color: '#81C784', fontWeight: 600 }}>
                    {formatWorkingHours(selectedDailyRecord.net_working_time || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFB74D', fontWeight: 600 }}>
                    {formatWorkingHours(selectedDailyRecord.total_break_time || 0)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Selfies Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#D4AF37' }}>
                  <Visibility sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                  Attendance Selfies
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>
                      Login Selfie
                    </Typography>
                    {selectedDailyRecord.login_selfie ? (
                      <SelfieImage src={selectedDailyRecord.login_selfie} alt="Login selfie" size={150} />
                    ) : (
                      <Box sx={{ width: 150, height: 150, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#B4C4A8' }}>No login selfie</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>
                      Logout Selfie
                    </Typography>
                    {selectedDailyRecord.logout_selfie ? (
                      <SelfieImage src={selectedDailyRecord.logout_selfie} alt="Logout selfie" size={150} />
                    ) : (
                      <Box sx={{ width: 150, height: 150, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#B4C4A8' }}>No logout selfie</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Breaks Section */}
              {selectedDailyRecord.breaks && selectedDailyRecord.breaks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37' }}>
                    <Coffee sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                    Break Details ({selectedDailyRecord.breaks.length})
                  </Typography>
                  {selectedDailyRecord.breaks.map((brk, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,183,77,0.1)', border: '1px solid rgba(255,183,77,0.3)', borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Break Type</Typography>
                          <Typography variant="body2" sx={{ color: '#FFB74D', textTransform: 'capitalize' }}>
                            {brk.break_type?.replace('_', ' ')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Duration</Typography>
                          <Typography variant="body2" sx={{ color: '#FFB74D' }}>
                            {formatWorkingHours(brk.duration || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Start Time</Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {brk.start_time ? new Date(brk.start_time).toLocaleTimeString() : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>End Time</Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {brk.end_time ? new Date(brk.end_time).toLocaleTimeString() : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>Start Selfie</Typography>
                          {brk.selfie_start ? (
                            <SelfieImage src={brk.selfie_start} alt="Break start" size={80} />
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>No selfie</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>End Selfie</Typography>
                          {brk.selfie_end ? (
                            <SelfieImage src={brk.selfie_end} alt="Break end" size={80} />
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>No selfie</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ bgcolor: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}>No attendance details available</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDailyDetailOpen(false)}
            sx={{ color: '#B4C4A8' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render daily report
  const renderDailyReport = () => {
    if (!reportData) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance data available for this month.</Alert>;
    }

    if (Array.isArray(reportData.dailyData)) {
      const sortedDays = sortDailyReportDays(reportData.dailyData);
      const monthTotals = reportData.monthTotals || {};
      const getDailyRows = (dayData) => {
        const attendanceRows = dayData.records || [];
        const attendanceEmployeeIds = new Set(attendanceRows.map(record => record.employee_id || record.employee?.id));

        const leaveRows = (dayData.leaveEmployees || [])
          .filter(emp => !attendanceEmployeeIds.has(emp.employee_id))
          .map(emp => ({
            id: `leave-${dayData.date}-${emp.employee_id}`,
            employee_id: emp.employee_id,
            employee: {
              id: emp.employee_id,
              first_name: emp.employee_details?.first_name || emp.employee_name?.split(' ')[0] || '',
              last_name: emp.employee_details?.last_name || emp.employee_name?.split(' ').slice(1).join(' ') || '',
              employee_id: emp.employee_details?.employee_id || '',
              department: emp.employee_details?.department || emp.department || '',
              email: emp.employee_details?.email || '',
            },
            date: dayData.date,
            status: 'on_leave',
            status_display: 'On Leave',
            net_working_time: 0,
            total_break_time: 0,
          }));

        const absentRows = (dayData.absentEmployees || [])
          .filter(emp => !attendanceEmployeeIds.has(emp.id))
          .map(emp => ({
            id: `absent-${dayData.date}-${emp.id}`,
            employee_id: emp.id,
            employee: {
              id: emp.id,
              first_name: emp.name?.split(' ')[0] || '',
              last_name: emp.name?.split(' ').slice(1).join(' ') || '',
              employee_id: emp.employee_id,
              department: emp.department || '',
              email: emp.email || '',
            },
            date: dayData.date,
            status: 'absent',
            status_display: 'Absent',
            net_working_time: 0,
            total_break_time: 0,
          }));

        return [...attendanceRows, ...leaveRows, ...absentRows];
      };

      return (
        <>
          <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                Monthly Report{reportData.monthLabel ? ` - ${reportData.monthLabel}` : ''}
              </Typography>
            </Box>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2 }}>
                    <Work sx={{ color: '#D4AF37', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalWorkingHours || '0h 0m'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#4CAF50', 0.1), borderRadius: 2 }}>
                    <CheckCircle sx={{ color: '#81C784', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#81C784', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalPresent || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#FF9800', 0.1), borderRadius: 2 }}>
                    <AccessTime sx={{ color: '#FFB74D', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#FFB74D', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalLate || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Late</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F44336', 0.1), borderRadius: 2 }}>
                    <Cancel sx={{ color: '#EF9A9A', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#EF9A9A', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalAbsent || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#2196F3', 0.1), borderRadius: 2 }}>
                    <EventBusy sx={{ color: '#90CAF9', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#90CAF9', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalOnLeave || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Leave</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#9C27B0', 0.1), borderRadius: 2 }}>
                    <HolidayVillage sx={{ color: '#CE93D8', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#CE93D8', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalHolidays || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Holidays</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {sortedDays.map((dayData) => (
            <Card key={dayData.date} sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Today sx={{ color: '#D4AF37' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#D4AF37' }}>
                      {formatDate(dayData.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      {dayData.dayName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {dayData.holidayCount > 0 && (
                      <Chip icon={<HolidayVillage sx={{ fontSize: 16 }} />} label={dayData.holidayInfo?.name || 'Holiday'} size="small" sx={{ bgcolor: alpha('#9C27B0', 0.2), color: '#CE93D8', border: '1px solid #CE93D8' }} />
                    )}
                    <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label={`${dayData.presentCount || 0} Present`} size="small" sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784', border: '1px solid #81C784' }} />
                    <Chip icon={<AccessTime sx={{ fontSize: 16 }} />} label={`${dayData.lateCount || 0} Late`} size="small" sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D', border: '1px solid #FFB74D' }} />
                    <Chip icon={<Cancel sx={{ fontSize: 16 }} />} label={`${dayData.absentCount || 0} Absent`} size="small" sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A', border: '1px solid #EF9A9A' }} />
                    {(dayData.onLeaveCount || 0) > 0 && (
                      <Chip icon={<EventBusy sx={{ fontSize: 16 }} />} label={`${dayData.onLeaveCount} Leave`} size="small" sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9', border: '1px solid #90CAF9' }} />
                    )}
                  </Box>
                </Box>
              </Box>
              {getDailyRows(dayData).length > 0 ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Break</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDailyRows(dayData).map((record, index) => {
                        const statusStyle = getStatusColor(record.status);
                        return (
                          <TableRow key={record.id || index} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                {record.employee?.first_name} {record.employee?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {record.employee?.employee_id}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.employee?.department || '-'}</TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.login_time ? new Date(record.login_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.logout_time ? new Date(record.logout_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(record.net_working_time || 0)}</TableCell>
                            <TableCell sx={{ color: '#F57C00' }}>{formatWorkingHours(record.total_break_time || 0)}</TableCell>
                            <TableCell>
                              {isAdmin && handleMarkEmployeeStatusWithTimes ? (
                                <Chip
                                  icon={statusStyle.icon}
                                  label={record.status?.replace('_', ' ').toUpperCase() || 'Present'}
                                  size="small"
                                  onClick={(e) => openStatusMenu(e, record.employee?.id || record.employee_id, record.date || dayData.date)}
                                  clickable
                                  sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                />
                              ) : (
                                <Chip
                                  icon={statusStyle.icon}
                                  label={record.status?.replace('_', ' ').toUpperCase() || 'Present'}
                                  size="small"
                                  sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openDailyDetail(record.employee, getDailyRows(dayData))}
                                sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem', borderColor: '#D4AF37', color: '#D4AF37' }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8', fontStyle: 'italic' }}>
                    {dayData.holidayCount > 0 ? 'Holiday - No attendance records' : 'No attendance records for this day'}
                  </Typography>
                </Box>
              )}
            </Card>
          ))}
        </>
      );
    }

    const isNewFormat = reportData.employees !== undefined && Array.isArray(reportData.employees);

    if (isNewFormat) {
      const dayData = {
        date: reportData.date,
        records: (reportData.employees || []).map(emp => ({
          id: emp.attendance_id,
          employee: {
            id: emp.id,
            first_name: emp.first_name,
            last_name: emp.last_name,
            employee_id: emp.employee_id,
            department: emp.department,
            email: emp.email,
          },
          login_time: emp.login_time,
          logout_time: emp.logout_time,
          login_selfie: emp.login_selfie,
          logout_selfie: emp.logout_selfie,
          breaks: emp.breaks || [],
          status: emp.status === 'on_leave' ? 'on_leave' : (emp.login_time ? (emp.status === 'late' ? 'late' : 'present') : 'absent'),
          net_working_time: emp.working_hours,
          total_break_time: emp.break_time,
        })),
        presentCount: reportData.totals?.present || 0,
        absentCount: reportData.totals?.absent || 0,
        lateCount: reportData.totals?.late || 0,
        onLeaveCount: reportData.leaves?.length || 0,
        holidayCount: reportData.holiday ? 1 : 0,
        holidayInfo: reportData.holiday,
      };

      return (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#D4AF37' }}>
            <Today sx={{ mr: 1, verticalAlign: 'middle' }} />
            Daily Report - {formatDate(reportData.date)}
          </Typography>

          {dayData.records.length > 0 ? (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Break</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayData.records.map((row) => {
                    const statusStyle = getStatusColor(row.status);
                    return (
                      <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                            {row.employee?.first_name} {row.employee?.last_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                            {row.employee?.employee_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{row.employee?.department || '-'}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{row.login_time ? new Date(row.login_time).toLocaleTimeString() : '-'}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{row.logout_time ? new Date(row.logout_time).toLocaleTimeString() : '-'}</TableCell>
                        <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(row.net_working_time || 0)}</TableCell>
                        <TableCell sx={{ color: '#F57C00' }}>{formatWorkingHours(row.total_break_time || 0)}</TableCell>
                        <TableCell>
                          {isAdmin && handleMarkEmployeeStatusWithTimes ? (
                            <Chip
                              icon={statusStyle.icon}
                              label={row.status === 'present' ? 'Present' : row.status === 'late' ? 'Late' : row.status === 'on_leave' ? 'On Leave' : 'Absent'}
                              size="small"
                              onClick={(e) => openStatusMenu(e, row.employee?.id, reportDate || row.date)}
                              clickable
                              sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            />
                          ) : (
                            <Chip
                              icon={statusStyle.icon}
                              label={row.status === 'present' ? 'Present' : row.status === 'late' ? 'Late' : row.status === 'on_leave' ? 'On Leave' : 'Absent'}
                              size="small"
                              sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.color}` }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openDailyDetail(row.employee, dayData.records)}
                            sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem', borderColor: '#D4AF37', color: '#D4AF37' }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance records for this day.</Alert>
          )}
        </>
      );
    }

    // Fallback for older format
    const employeesList = reportData.attendance || reportData.data || [];
    if (!employeesList.length) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance data available.</Alert>;
    }

    return (
      <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login Time</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout Time</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working Hours</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employeesList.map((row) => {
              const statusStyle = getStatusColor(row.status);
              return (
                <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {row.employee?.first_name} {row.employee?.last_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#B4C4A8' }}>{row.employee?.department || '-'}</TableCell>
                  <TableCell sx={{ color: '#B4C4A8' }}>{row.login_time ? new Date(row.login_time).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell sx={{ color: '#B4C4A8' }}>{row.logout_time ? new Date(row.logout_time).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(row.net_working_time || 0)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={statusStyle.icon}
                      label={row.status_display || (row.status === 'present' ? 'Present' : row.status === 'late' ? 'Late' : 'Absent')}
                      size="small"
                      sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render monthly report
  const renderMonthlyReport = () => {
    if (!reportData || (!reportData.summary && !reportData.employees)) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No monthly data available.</Alert>;
    }

    let employeeAttendance = [];

    if (reportData.summary && Array.isArray(reportData.summary)) {
      employeeAttendance = reportData.summary.map(emp => {
        const leaveDays = calculateLeaveDays(emp.leaves, selectedMonth, selectedYear);
        return {
          ...emp,
          present: emp.daysPresent || 0,
          late: emp.daysLate || 0,
          absent: emp.daysAbsent || 0,
          leave: leaveDays,
          holiday: 0,
          weekend: 0,
          totalWorkingHours: emp.totalWorkingHours || '0h 0m',
          totalBreakHours: emp.totalBreakTime || '0h 0m',
          attendancePercentage: emp.attendancePercentage || 0,
        };
      });
    } else if (reportData.employees && Array.isArray(reportData.employees)) {
      employeeAttendance = reportData.employees.map(emp => ({
        employee: {
          id: emp.id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          employee_id: emp.employee_id,
          department: emp.department,
        },
        present: emp.status === 'present' ? 1 : 0,
        late: emp.status === 'late' ? 1 : 0,
        absent: emp.status === 'absent' ? 1 : 0,
        leave: 0,
        totalWorkingHours: formatWorkingHours(emp.working_hours || 0),
        totalBreakHours: formatWorkingHours(emp.break_time || 0),
        attendancePercentage: emp.status === 'present' || emp.status === 'late' ? 100 : 0,
      }));
    }

    const monthTotals = reportData.monthTotals || {};
    const monthName = getMonthName(selectedMonth);

    return (
      <>
        {!isAdmin && (
          <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                {monthName} {selectedYear} - Monthly Summary
              </Typography>
            </Box>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2 }}>
                    <Work sx={{ color: '#D4AF37', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#D4AF37', fontWeight: 700, mt: 1, fontSize: '1rem' }}>
                      {monthTotals.totalWorkingHours || '0h 0m'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F57C00', 0.1), borderRadius: 2 }}>
                    <Schedule sx={{ color: '#F57C00', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#F57C00', fontWeight: 700, mt: 1, fontSize: '1rem' }}>
                      {monthTotals.totalBreakTime || '0h 0m'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#4CAF50', 0.1), borderRadius: 2 }}>
                    <CheckCircle sx={{ color: '#81C784', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#81C784', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalPresent || employeeAttendance.reduce((sum, e) => sum + e.present, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#FF9800', 0.1), borderRadius: 2 }}>
                    <AccessTime sx={{ color: '#FFB74D', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#FFB74D', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalLate || employeeAttendance.reduce((sum, e) => sum + e.late, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Late Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F44336', 0.1), borderRadius: 2 }}>
                    <Cancel sx={{ color: '#EF9A9A', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#EF9A9A', fontWeight: 700, mt: 1 }}>
                      {monthTotals.totalAbsent || employeeAttendance.reduce((sum, e) => sum + e.absent, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#9C27B0', 0.1), borderRadius: 2 }}>
                    <HolidayVillage sx={{ color: '#CE93D8', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#CE93D8', fontWeight: 700, mt: 1 }}>
                      {reportData.holidaysCount || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Holidays</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'rgba(212,175,55,0.3)', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': { color: '#B4C4A8', '&.Mui-selected': { color: '#D4AF37' } },
              '& .MuiTabs-indicator': { backgroundColor: '#D4AF37' }
            }}
          >
            <Tab label={<><BarChart sx={{ mr: 1, fontSize: 18 }} /> Employee Overview</>} />
            <Tab label={<><CalendarViewMonth sx={{ mr: 1, fontSize: 18 }} /> Daily Details</>} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                  <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                  All Employees Status
                </Typography>
                {isAdmin && selectedEmployeesBulk && selectedEmployeesBulk.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleBulkLeaveRequest}
                      disabled={bulkActionLoading}
                      sx={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                    >
                      Bulk Leave
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleBulkPrintLocal && handleBulkPrintLocal(selectedEmployeesBulk)}
                      sx={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                    >
                      Print Selected
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                    {isAdmin && (
                      <TableCell sx={{ fontWeight: 600, color: '#D4AF37', width: 50 }}>
                        Select
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 200 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Present</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Late</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Absent</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Leave</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Working Hrs</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Attendance %</TableCell>
                    {isAdmin && <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeAttendance.map((emp, idx) => (
                    <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                      {isAdmin && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedEmployeesBulk?.includes(emp.employee?.id)}
                            onChange={(e) => {
                              const employeeId = emp.employee?.id;
                              if (e.target.checked) {
                                setSelectedEmployeesBulk(prev => [...(prev || []), employeeId]);
                              } else {
                                setSelectedEmployeesBulk(prev => (prev || []).filter(id => id !== employeeId));
                              }
                            }}
                            className="accent-gold"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                          {emp.employee?.first_name} {emp.employee?.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                          {emp.employee?.employee_id} | {emp.employee?.department || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={emp.present} size="small" sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={emp.late} size="small" sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={emp.absent} size="small" sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={emp.leave} size="small" sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#D4AF37', fontWeight: 500 }}>{emp.totalWorkingHours}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Typography variant="body2" sx={{ color: '#81C784', fontWeight: 500 }}>{emp.attendancePercentage}%</Typography>
                        </Box>
                      </TableCell>
                      {isAdmin && (
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openEmployeeReport(emp.employee)}
                              sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem', borderColor: '#D4AF37', color: '#D4AF37' }}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {activeTab === 1 && reportData.dailyData && (
          <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                <CalendarViewMonth sx={{ mr: 1, verticalAlign: 'middle' }} />
                Daily Breakdown
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Present</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Late</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Absent</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Leave</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Holiday</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(reportData.dailyData || []).map((day, idx) => (
                    <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                      <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(day.date)}</TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>{day.dayName}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={day.presentCount || 0} size="small" sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784' }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={day.lateCount || 0} size="small" sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D' }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={day.absentCount || 0} size="small" sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A' }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={day.onLeaveCount || 0} size="small" sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9' }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {day.holidayCount > 0 ? (
                          <Chip label={day.holidayInfo?.name || 'Holiday'} size="small" sx={{ bgcolor: alpha('#9C27B0', 0.2), color: '#CE93D8' }} />
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const records = day.records || [];
                              setSelectedDayAttendance(records);
                              setLoadingDayDetail(false);
                              setDayDetailOpen(true);
                            }}
                            sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem', borderColor: '#D4AF37', color: '#D4AF37' }}
                          >
                            View
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Day Detail Dialog */}
        <Dialog
          open={dayDetailOpen}
          onClose={() => setDayDetailOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Day Attendance Details
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {loadingDayDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#D4AF37' }} />
              </Box>
            ) : (
              selectedDayAttendance && selectedDayAttendance.length > 0 ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Breaks</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login Selfie</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout Selfie</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedDayAttendance.map((att) => {
                        const statusStyle = getStatusColor(att.status);
                        const breaks = att.breaks || [];
                        return (
                          <TableRow key={att.id} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                            <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(att.date)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                {att.employee?.first_name} {att.employee?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {att.employee?.employee_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={statusStyle.icon}
                                label={att.status?.replace('_', ' ').toUpperCase()}
                                size="small"
                                sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{att.login_time ? new Date(att.login_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{att.logout_time ? new Date(att.logout_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(att.net_working_time || 0)}</TableCell>
                            <TableCell>
                              <Box>
                                {breaks.length > 0 ? breaks.map((brk, bIdx) => (
                                  <Chip
                                    key={bIdx}
                                    label={`${brk.break_type} (${formatWorkingHours(brk.duration || 0)})`}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5, bgcolor: alpha('#FFB74D', 0.2), color: '#FFB74D' }}
                                  />
                                )) : '-'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {att.login_selfie ? (
                                <SelfieImage src={att.login_selfie} alt="Login selfie" size={60} />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {att.logout_selfie ? (
                                <SelfieImage src={att.logout_selfie} alt="Logout selfie" size={60} />
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ bgcolor: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}>No attendance details available</Alert>
              ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDayDetailOpen(false)}
              sx={{ color: '#B4C4A8' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Employee Full Report Dialog */}
        <Dialog
          open={employeeReportOpen}
          onClose={() => setEmployeeReportOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Full Attendance Report - {selectedEmployeeReport && selectedEmployeeReport.length > 0 ? `${selectedEmployeeReport[0]?.employee?.first_name || ''} ${selectedEmployeeReport[0]?.employee?.last_name || ''}` : ''} ({getMonthName(selectedMonth)} {selectedYear})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {loadingEmployeeReport ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#D4AF37' }} />
              </Box>
            ) : (
              selectedEmployeeReport && selectedEmployeeReport.length > 0 ? (
                <>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {(() => {
                      const totalWorking = selectedEmployeeReport.reduce((sum, att) => sum + (att.net_working_time || 0), 0);
                      const totalBreak = selectedEmployeeReport.reduce((sum, att) => sum + (att.total_break_time || 0), 0);
                      const presentDays = selectedEmployeeReport.filter(att => att.status === 'present' || att.login_time).length;
                      const lateDays = selectedEmployeeReport.filter(att => att.status === 'late').length;
                      const absentDays = selectedEmployeeReport.filter(att => att.status === 'absent').length;
                      const leaveDays = selectedEmployeeReport.filter(att => att.status === 'on_leave').length;

                      return [
                        { label: 'Working Hours', value: formatWorkingHours(totalWorking), icon: <Work />, color: '#D4AF37' },
                        { label: 'Break Time', value: formatWorkingHours(totalBreak), icon: <Schedule />, color: '#F57C00' },
                        { label: 'Present Days', value: presentDays.toString(), icon: <CheckCircle />, color: '#81C784' },
                        { label: 'Late Days', value: lateDays.toString(), icon: <AccessTime />, color: '#FFB74D' },
                        { label: 'Absent Days', value: absentDays.toString(), icon: <Cancel />, color: '#EF9A9A' },
                        { label: 'Leave Days', value: leaveDays.toString(), icon: <EventBusy />, color: '#2196F3' },
                      ];
                    })().map((stat, idx) => (
                      <Grid item xs={6} sm={2} key={idx}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha(stat.color, 0.1), borderRadius: 2 }}>
                          <Box component="span" sx={{ color: stat.color, fontSize: 28 }}>{stat.icon}</Box>
                          <Typography variant="h5" sx={{ color: stat.color, fontWeight: 700, mt: 1, fontSize: '1rem' }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>{stat.label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Breaks</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login Selfie</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout Selfie</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedEmployeeReport.map((att) => {
                          const statusStyle = getStatusColor(att.status);
                          const breaks = att.breaks || [];
                          return (
                            <TableRow key={att.id} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                              <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(att.date)}</TableCell>
                              <TableCell>
                                {isAdmin && handleMarkEmployeeStatusWithTimes ? (
                                  <Chip
                                    icon={statusStyle.icon}
                                    label={att.status?.replace('_', ' ').toUpperCase()}
                                    size="small"
                                    onClick={(e) => openStatusMenu(e, selectedEmployeeReport[0]?.employee?.id, att.date)}
                                    clickable
                                    sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                  />
                                ) : (
                                  <Chip
                                    icon={statusStyle.icon}
                                    label={att.status?.replace('_', ' ').toUpperCase()}
                                    size="small"
                                    sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={{ color: '#B4C4A8' }}>{att.login_time ? new Date(att.login_time).toLocaleTimeString() : '-'}</TableCell>
                              <TableCell sx={{ color: '#B4C4A8' }}>{att.logout_time ? new Date(att.logout_time).toLocaleTimeString() : '-'}</TableCell>
                              <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(att.net_working_time || 0)}</TableCell>
                              <TableCell>
                                <Box>
                                  {breaks.length > 0 ? breaks.map((brk, bIdx) => (
                                    <Chip
                                      key={bIdx}
                                      label={`${brk.break_type} (${formatWorkingHours(brk.duration || 0)})`}
                                      size="small"
                                      sx={{ mr: 0.5, mb: 0.5, bgcolor: alpha('#FFB74D', 0.2), color: '#FFB74D' }}
                                    />
                                  )) : '-'}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {att.login_selfie ? (
                                  <SelfieImage src={att.login_selfie} alt="Login selfie" size={60} />
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {att.logout_selfie ? (
                                  <SelfieImage src={att.logout_selfie} alt="Logout selfie" size={60} />
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Breaks with selfies */}
                  {selectedEmployeeReport.some(att => att.breaks && att.breaks.length > 0) && (
                    <>
                      <Typography variant="h6" sx={{ mt: 4, mb: 2, color: '#D4AF37' }}>
                        <Coffee sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Break Details
                      </Typography>
                      <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Break Type</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Start</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>End</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Duration</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Start Selfie</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>End Selfie</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedEmployeeReport.flatMap((att) =>
                              (att.breaks || []).map((brk) => (
                                <TableRow key={`${att.date}-${brk.break_type}`} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                                  <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(att.date)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={brk.break_type?.replace('_', ' ').toUpperCase()}
                                      size="small"
                                      sx={{ bgcolor: alpha('#FFB74D', 0.2), color: '#FFB74D' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ color: '#B4C4A8' }}>{brk.start_time ? new Date(brk.start_time).toLocaleTimeString() : '-'}</TableCell>
                                  <TableCell sx={{ color: '#B4C4A8' }}>{brk.end_time ? new Date(brk.end_time).toLocaleTimeString() : '-'}</TableCell>
                                  <TableCell sx={{ color: '#D4AF37' }}>{formatWorkingHours(brk.duration || 0)}</TableCell>
                                  <TableCell>
                                    {brk.selfie_start ? (
                                      <SelfieImage src={brk.selfie_start} alt="Break start" size={60} />
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {brk.selfie_end ? (
                                      <SelfieImage src={brk.selfie_end} alt="Break end" size={60} />
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ bgcolor: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}>No attendance data available</Alert>
              ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setEmployeeReportOpen(false)}
              sx={{ color: '#B4C4A8' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  // Render custom range report
  const renderCustomRangeReport = () => {
    if (!reportData || !reportData.attendance) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance data available for the selected date range.</Alert>;
    }

    const attendanceList = reportData.attendance || [];
    if (!attendanceList.length) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance records found in the selected date range.</Alert>;
    }

    return (
      <>
        <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
            <Typography variant="h6" sx={{ color: '#D4AF37' }}>
              <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
              Custom Range Report: {customStartDate} to {customEndDate}
            </Typography>
          </Box>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2 }}>
                  <Work sx={{ color: '#D4AF37', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#D4AF37', fontWeight: 700, mt: 1, fontSize: '1rem' }}>
                    {reportData.summary?.totalWorkingHours || '0h 0m'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F57C00', 0.1), borderRadius: 2 }}>
                  <Schedule sx={{ color: '#F57C00', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#F57C00', fontWeight: 700, mt: 1, fontSize: '1rem' }}>
                    {reportData.summary?.totalBreakTime || '0h 0m'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#4CAF50', 0.1), borderRadius: 2 }}>
                  <CheckCircle sx={{ color: '#81C784', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#81C784', fontWeight: 700, mt: 1 }}>
                    {reportData.summary?.present || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present Days</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F44336', 0.1), borderRadius: 2 }}>
                  <Cancel sx={{ color: '#EF9A9A', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#EF9A9A', fontWeight: 700, mt: 1 }}>
                    {reportData.summary?.absent || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent Days</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#2196F3', 0.1), borderRadius: 2 }}>
                  <People sx={{ color: '#90CAF9', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#90CAF9', fontWeight: 700, mt: 1 }}>
                    {attendanceList.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Total Records</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#9C27B0', 0.1), borderRadius: 2 }}>
                  <DateRange sx={{ color: '#CE93D8', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#CE93D8', fontWeight: 700, mt: 1 }}>
                    {Math.ceil((new Date(customEndDate) - new Date(customStartDate)) / (1000 * 60 * 60 * 24)) + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Days in Range</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working Hours</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Break Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceList.map((row) => {
                const statusStyle = getStatusColor(row.status);
                return (
                  <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                    <TableCell sx={{ color: '#B4C4A8' }}>{formatDate(row.date)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                        {row.employee?.first_name} {row.employee?.last_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                        {row.employee?.employee_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#B4C4A8' }}>{row.login_time ? new Date(row.login_time).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell sx={{ color: '#B4C4A8' }}>{row.logout_time ? new Date(row.logout_time).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(row.net_working_time || 0)}</TableCell>
                    <TableCell sx={{ color: '#F57C00' }}>{formatWorkingHours(row.total_break_time || 0)}</TableCell>
                    <TableCell>
                      {isAdmin && handleMarkEmployeeStatusWithTimes ? (
                        <Chip
                          icon={statusStyle.icon}
                          label={row.status_display || (row.status === 'present' ? 'Present' : row.status === 'late' ? 'Late' : row.status === 'absent' ? 'Absent' : row.status === 'on_leave' ? 'On Leave' : 'No Record')}
                          size="small"
                          onClick={(e) => openStatusMenu(e, row.employee?.id || row.employee_id, reportDate || row.date)}
                          clickable
                          sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                        />
                      ) : (
                        <Chip
                          icon={statusStyle.icon}
                          label={row.status_display || (row.status === 'present' ? 'Present' : row.status === 'late' ? 'Late' : row.status === 'absent' ? 'Absent' : row.status === 'on_leave' ? 'On Leave' : 'No Record')}
                          size="small"
                          sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Render weekly report
  const renderWeeklyReport = () => {
    if (!reportData) {
      return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No attendance data available for this week.</Alert>;
    }

    const isNewFormat = reportData.dailyData !== undefined;

    if (isNewFormat) {
      const weekTotals = reportData.weekTotals || {};
      const sortedDays = [...(reportData.dailyData || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

      return (
        <>
          <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                Week: {formatDate(reportData.weekStart)} - {formatDate(reportData.weekEnd)}
              </Typography>
            </Box>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2 }}>
                    <Work sx={{ color: '#D4AF37', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#D4AF37', fontWeight: 700, mt: 1 }}>
                      {weekTotals.totalWorkingHours || '0h 0m'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F57C00', 0.1), borderRadius: 2 }}>
                    <Schedule sx={{ color: '#F57C00', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#F57C00', fontWeight: 700, mt: 1 }}>
                      {weekTotals.totalBreakTime || '0h 0m'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#4CAF50', 0.1), borderRadius: 2 }}>
                    <CheckCircle sx={{ color: '#81C784', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#81C784', fontWeight: 700, mt: 1 }}>
                      {weekTotals.totalPresent || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#FF9800', 0.1), borderRadius: 2 }}>
                    <AccessTime sx={{ color: '#FFB74D', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#FFB74D', fontWeight: 700, mt: 1 }}>
                      {weekTotals.totalLate || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Late Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#F44336', 0.1), borderRadius: 2 }}>
                    <Cancel sx={{ color: '#EF9A9A', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#EF9A9A', fontWeight: 700, mt: 1 }}>
                      {weekTotals.totalAbsent || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent Days</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha('#9C27B0', 0.1), borderRadius: 2 }}>
                    <HolidayVillage sx={{ color: '#CE93D8', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: '#CE93D8', fontWeight: 700, mt: 1 }}>
                      {reportData.holidaysCount || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Holidays</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#D4AF37' }}>
              <CalendarViewMonth sx={{ mr: 1, verticalAlign: 'middle' }} />
              Daily Breakdown ({sortedDays.length} days)
            </Typography>
          </Box>

          {sortedDays.map((dayData) => (
            <Card key={dayData.date} sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: alpha('#D4AF37', 0.05), borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Today sx={{ color: '#D4AF37' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#D4AF37' }}>
                      {formatDate(dayData.date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {dayData.holidayCount > 0 && (
                      <Chip icon={<HolidayVillage sx={{ fontSize: 16 }} />} label="Holiday" size="small" sx={{ bgcolor: alpha('#9C27B0', 0.2), color: '#CE93D8', border: '1px solid #CE93D8' }} />
                    )}
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      label={`${dayData.presentCount} Present`}
                      size="small"
                      sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784', border: '1px solid #81C784' }}
                    />
                    <Chip
                      icon={<AccessTime sx={{ fontSize: 16 }} />}
                      label={`${dayData.lateCount} Late`}
                      size="small"
                      sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D', border: '1px solid #FFB74D' }}
                    />
                    <Chip
                      icon={<Cancel sx={{ fontSize: 16 }} />}
                      label={`${dayData.absentCount} Absent`}
                      size="small"
                      sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A', border: '1px solid #EF9A9A' }}
                    />
                    {dayData.onLeaveCount > 0 && (
                      <Chip
                        icon={<EventBusy sx={{ fontSize: 16 }} />}
                        label={`${dayData.onLeaveCount} Leave`}
                        size="small"
                        sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9', border: '1px solid #90CAF9' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              {dayData.records && dayData.records.length > 0 ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dayData.records.map((record, rIdx) => {
                        const statusStyle = getStatusColor(record.status);
                        return (
                          <TableRow key={rIdx} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                {record.employee?.first_name} {record.employee?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {record.employee?.employee_id}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.employee?.department || '-'}</TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.login_time ? new Date(record.login_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#B4C4A8' }}>{record.logout_time ? new Date(record.logout_time).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell sx={{ color: '#D4AF37', fontWeight: 500 }}>{formatWorkingHours(record.net_working_time || 0)}</TableCell>
                            <TableCell>
                              <Chip
                                icon={statusStyle.icon}
                                label={record.status?.replace('_', ' ').toUpperCase() || 'Present'}
                                size="small"
                                sx={{ bgcolor: statusStyle.bg, color: statusStyle.color }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8', fontStyle: 'italic' }}>
                    {dayData.holidayCount > 0 ? 'Holiday - No attendance records' : 'No attendance records for this day'}
                  </Typography>
                </Box>
              )}
            </Card>
          ))}
        </>
      );
    }

    return <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No weekly report data available.</Alert>;
  };

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#D4AF37' }} />
        </Box>
      )}

      {!loading && reportType === 'daily' && renderDailyReport()}
      {!loading && reportType === 'weekly' && renderWeeklyReport()}
      {!loading && reportType === 'monthly' && renderMonthlyReport()}
      {!loading && reportType === 'employee_wise' && renderMonthlyReport()}
      {!loading && reportType === 'custom_range' && renderCustomRangeReport()}

      {/* Daily Detail Dialog - Shared by both daily report branches */}
      {renderDailyDetailDialog()}

      {/* Status Change Dropdown Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={closeStatusMenu}
        PaperProps={{
          sx: {
            bgcolor: '#0F2744',
            border: '1px solid #D4AF37',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem onClick={() => handleStatusChangeFromMenu('present')} sx={{ color: '#81C784', '&:hover': { bgcolor: alpha('#4CAF50', 0.1) } }}>
          <CheckCircle sx={{ mr: 1, fontSize: 18 }} /> Mark Present
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('absent')} sx={{ color: '#EF9A9A', '&:hover': { bgcolor: alpha('#F44336', 0.1) } }}>
          <Cancel sx={{ mr: 1, fontSize: 18 }} /> Mark Absent
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('late')} sx={{ color: '#FFB74D', '&:hover': { bgcolor: alpha('#FF9800', 0.1) } }}>
          <AccessTime sx={{ mr: 1, fontSize: 18 }} /> Mark Late
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('on_leave')} sx={{ color: '#90CAF9', '&:hover': { bgcolor: alpha('#2196F3', 0.1) } }}>
          <EventBusy sx={{ mr: 1, fontSize: 18 }} /> Mark On Leave
        </MenuItem>
      </Menu>
    </>
  );
};

export default ReportContent;
